<script setup>
import { computed, nextTick, ref, watch, onMounted } from 'vue';
import { Avatar } from '@dashhudson/dashing-ui/avatar';
import { Button } from '@dashhudson/dashing-ui/button';
import { EmojiPicker } from '@dashhudson/dashing-ui/emoji-picker';
import { FormItem } from '@dashhudson/dashing-ui/form';
import { RichTextarea } from '@dashhudson/dashing-ui/rich-textarea';
import { ToggleSwitch } from '@dashhudson/dashing-ui/toggle-switch';
import { useConfirmationDialog } from '@dashhudson/dashing-ui/confirmation-dialog';
import SavedRepliesDropdown from '@/app/community/components/SavedRepliesDropdown.vue';
import MentionDetails from '@/app/community/components/MentionDetails.vue';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import { useCommunityTranslationStore } from '@/stores/community-translation';
import {
  communityMessageInteractionEvent,
  getBrandHandleFromInteraction,
  internalNoteCreateEvent,
  messageAssignedEvent,
  translationEvent,
} from '@/app/community/utils/mixpanel-events';
import { getLanguageName } from '@/utils/countries';
import {
  COMMUNITY_INTERACTION_ACTIONS,
  COMMUNITY_INTERACTION_GDI_REPLY_LIMIT,
  COMMUNITY_INTERACTION_PLATFORM_BY_TYPE,
  COMMUNITY_INTERACTION_TYPES_WITH_ADS,
  COMMUNITY_ROUTE_NAMES,
  replyTranslationConfirmation,
} from '@/app/community/constants';
import { useReplyHandler } from '@/app/community/composables/useReplyHandler';
import { useCommunityStore } from '@/stores/community';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useIdentityStore } from '@/stores/identity';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import humps from 'humps';
import { getUserInitials } from '@/utils/user';

const props = defineProps({
  interaction: { type: Object, required: true },
  disabled: { type: Boolean, default: undefined },
  showActions: { type: Boolean, default: true },
});

const emits = defineEmits(['expand-on-reply']);

const translationStore = useCommunityTranslationStore();
const communityStore = useCommunityStore();
const interactionStore = useCommunityInteractionStore();
const authStore = useAuthStore();
const identityStore = useIdentityStore();
const multiBrandStore = useCommunityMultiBrandsStore();

const route = useRoute();
const { submitReply } = useReplyHandler(props.interaction);
const { canAccessCommunityTranslation } = useCommunityPermissions();
const { showConfirmationDialog } = useConfirmationDialog({
  group: 'global',
  onAccept: null,
  onReject: null,
});

const replyComposer = ref(null);
const internalNoteMode = ref(false);
const submitLoading = ref(false);
const inputTouched = ref(false);
const showReplies = ref(false);
const isFocused = ref(false);

const translating = ref(false);
const translated = ref(false);
const previousComposerText = ref('');
const previousDetectedLanguage = ref();

const internalNotePage = computed(() => route.name === COMMUNITY_ROUTE_NAMES.INTERNAL_NOTES);
const variant = computed(() => (internalNoteMode.value ? 'internalNote' : undefined));

const platform = computed(
  () => COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[props.interaction.type]?.platformValue,
);

const prefilledText = computed(() => {
  const t = props.interaction.type;
  if (
    [
      COMMUNITY_INTERACTION_TYPES_WITH_ADS.INSTAGRAM_COMMENT,
      COMMUNITY_INTERACTION_TYPES_WITH_ADS.FACEBOOK_COMMENT,
    ].includes(t) &&
    !internalNoteMode.value
  ) {
    const usernameKey = `${platform.value}Username`;
    return `@${props.interaction[usernameKey]} `;
  }
  return '';
});

const interactionText = ref(prefilledText.value);
const internalNoteText = ref('');

const mentions = ref({});

function stripHtml(html) {
  const replaceBreaks = html.replace(/<br>/g, '\n');
  const tmp = document.createElement('div');
  tmp.innerHTML = replaceBreaks;
  return tmp.textContent || '';
}

const composerText = computed({
  get() {
    return internalNoteMode.value ? internalNoteText.value : interactionText.value;
  },
  set(val) {
    if (internalNoteMode.value) internalNoteText.value = val;
    else interactionText.value = val;
  },
});

const plainText = computed(() => {
  if (translated.value && !internalNoteMode.value) {
    return stripHtml(composerText.value);
  }

  return replyComposer.value?.textContent || stripHtml(composerText.value);
});

const maxLength = computed(() =>
  internalNoteMode.value
    ? 1000
    : COMMUNITY_INTERACTION_GDI_REPLY_LIMIT[props.interaction.type] || 1000,
);

const maxLengthHint = computed(() => {
  const limit = COMMUNITY_INTERACTION_GDI_REPLY_LIMIT[props.interaction.type];
  return limit != null ? `Maximum ${limit} characters allowed` : null;
});

const brandName = computed(
  () => identityStore.identityBrandsById[props.interaction?.brandId]?.name ?? '',
);

const placeholder = computed(() =>
  internalNoteMode.value
    ? 'Write an internal note or @mention a teammate'
    : `Send a reply from ${brandName.value}`,
);
const sendDisabled = computed(
  () =>
    props.disabled ||
    plainText.value?.trim().length === 0 ||
    plainText.value?.trim() === prefilledText.value.trim() ||
    plainText.value?.trim().length > maxLength.value,
);

const disableTranslation = computed(
  () =>
    props.disabled ||
    translating.value ||
    plainText.value?.trim().length === 0 ||
    plainText.value?.trim() === prefilledText.value.trim(),
);

const translationTargetId = computed(() => {
  if (props.interaction.type.includes('CONVERSATION')) {
    return translationStore.findTranslationMapItemId(props.interaction);
  }
  return props.interaction?.id;
});
const translationData = computed(() => {
  if (translationTargetId.value) {
    return translationStore.translationMap[translationTargetId.value];
  }
  return {};
});

const suggestionUsers = computed(() => {
  if (interactionStore.selectedCommunityInteraction?.brandId !== authStore.currentBrand.id) {
    return multiBrandStore.brandIdToAccessibleBrandUsersMap[
      interactionStore.selectedCommunityInteraction?.brandId
    ];
  }
  return authStore.brandAccessibleUsers;
});

function focusEnd() {
  replyComposer.value?.editor?.commands?.focus('end');
}

function onComposerFocus() {
  isFocused.value = true;
  if (!inputTouched.value) nextTick(() => focusEnd());
}

async function translateReplyText() {
  if (!translated.value) {
    const { detectedLanguage } = translationData.value;

    if (
      previousComposerText.value.trim() === plainText.value &&
      detectedLanguage === previousDetectedLanguage.value
    ) {
      translated.value = true;
      return;
    }
    translating.value = true;
    const rawHtml = await translationStore.translateMessage(
      translationTargetId.value,
      composerText.value,
      false,
      true,
    );

    if (rawHtml) {
      previousComposerText.value = composerText.value;
      composerText.value = stripHtml(rawHtml);
      previousDetectedLanguage.value = detectedLanguage;
      translated.value = true;
    } else {
      translated.value = false;
    }
    translating.value = false;
    translationEvent(getLanguageName(detectedLanguage), props.interaction, true, true);
  } else {
    composerText.value = previousComposerText.value;
    translated.value = false;

    await nextTick(() => focusEnd());
  }
}

const translateMenuItem = computed(() => {
  if (
    canAccessCommunityTranslation.value &&
    !!translationTargetId.value &&
    !!translationData.value?.translatable
  ) {
    return [
      {
        icon: 'translate',
        label: translated.value
          ? 'Show Original'
          : `Translate to ${getLanguageName(translationData.value.detectedLanguage)}`,
        disabled: disableTranslation.value,
        active: translated.value,
        command: () => translateReplyText(),
      },
    ];
  }
  return [];
});
const replyMenuItems = computed(() => [
  {
    id: 'saved-replies-trigger',
    icon: 'message-text',
    label: 'Saved Replies',
    disabled: translated.value || translating.value,
    command: () => {
      showReplies.value = !showReplies.value;
    },
  },
  ...translateMenuItem.value,
]);
const menuItems = computed(() => [
  {
    id: 'reply-composer-emoji-picker-trigger',
    icon: 'emoji',
    label: 'Emoji',
    disabled: (translated.value || translating.value) && !internalNoteMode.value,
  },
  ...(!internalNoteMode.value ? replyMenuItems.value : []),
]);

function onMentionDetailsCallback(mentionId) {
  return mentions.value[mentionId];
}

function getUserAvatar(suggestionUser) {
  return suggestionUser.avatarUrl
    ? { image: suggestionUser.avatarUrl }
    : { label: getUserInitials(suggestionUser) };
}

function onMentionSearchCallback(query) {
  const trimmedQuery = query?.trim().toLowerCase();
  if (trimmedQuery) {
    return humps
      .camelizeKeys(suggestionUsers.value)
      .filter((user) => {
        const firstName = user.firstName?.toLowerCase();
        const lastName = user.lastName?.toLowerCase();
        const email = user.email?.toLowerCase();
        const fullName = `${firstName} ${lastName}`;
        return (
          firstName?.startsWith(trimmedQuery) ||
          lastName?.startsWith(trimmedQuery) ||
          fullName?.startsWith(trimmedQuery) ||
          email?.startsWith(trimmedQuery)
        );
      })
      .map((user) => ({
        ...user,
        label: `${user.firstName} ${user.lastName}`,
        avatarProps: getUserAvatar(user),
        value: user.id,
      }))
      .slice(0, 5);
  }
  return [];
}

function onMentionAddCallback(mention) {
  mentions.value[mention.value] = mention;
}

const mentionProps = computed(() =>
  internalNoteMode.value
    ? {
        enableMentions: true,
        mentionDetailsCallback: onMentionDetailsCallback,
        mentionDetailsComponent: MentionDetails,
        mentionSearchCallback: onMentionSearchCallback,
        mentionAddCallback: onMentionAddCallback,
      }
    : {},
);

async function confirmTranslation() {
  const targetId = translationStore.findTranslationMapItemId(props.interaction, true);
  const activeTranslation = translationStore.translationMap[targetId] ?? {};
  if (!translated.value && activeTranslation.isToggled) {
    const detected = getLanguageName(activeTranslation.detectedLanguage);
    const preferred = getLanguageName(translationStore.preferredLanguage);
    const { title, confirmMessage, confirmAlias, cancelAlias } = replyTranslationConfirmation(
      detected,
      preferred,
    );
    return showConfirmationDialog({
      header: title,
      message: confirmMessage,
      acceptLabel: confirmAlias,
      acceptSeverity: 'primary',
      rejectLabel: cancelAlias,
    });
  }
  return true;
}

function insertSnippet(text) {
  const rc = replyComposer.value;

  if (rc?.insertText) {
    rc.insertText(text);
    nextTick(() => rc.editor?.commands?.focus?.());
    return;
  }
  composerText.value += text;
}

function onEmojiPicked(emoji) {
  const char = emoji?.native ?? '';
  if (!char) return;

  insertSnippet(char);
}

async function submitInternalNote(text) {
  const parts = props.interaction.type.split('_');
  let messageType = parts[1];
  if (parts.length > 2) messageType = parts.slice(1).join('_');

  await communityStore.createInternalNote({
    brandId: props.interaction.brandId,
    resourceId: String(props.interaction.id),
    text,
    meta: {
      channel: parts[0],
      messageType,
      isRead: false,
      isAdComment: props.interaction.isAdComment || false,
    },
  });
}

async function handleSubmit() {
  const textToSend = plainText.value?.trim();
  if (submitLoading.value || sendDisabled.value || !textToSend) return;

  const canSubmitReply = await confirmTranslation();
  if (!canSubmitReply) return;

  submitLoading.value = true;
  try {
    if (internalNoteMode.value) {
      await submitInternalNote(textToSend);
    } else {
      const { data } = await submitReply(textToSend, false);
      if (data) {
        emits('expand-on-reply');
        await interactionStore.setCommunityInteractionRead();
      }
    }
  } finally {
    composerText.value = prefilledText.value ?? '';

    submitLoading.value = false;
    translated.value = false;
    previousComposerText.value = '';
    previousDetectedLanguage.value = undefined;
  }

  // Send mixpanel events
  if (internalNoteMode.value) {
    internalNoteCreateEvent(
      props.interaction.type,
      props.interaction.id,
      props.interaction.brandId,
      getBrandHandleFromInteraction(props.interaction),
      props.interaction.isAdComment || false,
    );
  } else {
    const { platform: interactionPlatform, messageType } =
      COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[props.interaction.type];
    const isConversation = props.interaction.type.includes('CONVERSATION');
    communityMessageInteractionEvent({
      platform: interactionPlatform,
      messageType,
      textToSend,
      actionPerformed: COMMUNITY_INTERACTION_ACTIONS.POSTED,
      brandHandle: isConversation ? props.interaction.brandParticipant?.username : undefined,
      communityInteraction: props.interaction,
    });
  }
}

watch(
  () => plainText.value,
  async (newText) => {
    if (isFocused.value) {
      if (
        !inputTouched.value &&
        newText.trim().length > prefilledText.value.trim().length &&
        !props.disabled &&
        !interactionStore.blockActionClicked &&
        interactionStore.selectedCommunityInteraction.assigneeId === null
      ) {
        await communityStore.createCommunityAssignment(
          {
            brandId: props.interaction.brandId,
            interactionId: props.interaction.id,
            assignmentPayload: {
              assignedToUser: authStore.identity.id,
              assignedByUser: authStore.identity.id,
            },
          },
          route?.name === COMMUNITY_ROUTE_NAMES.ASSIGNED_TO_YOU,
          false,
        );

        messageAssignedEvent({
          messageType: props.interaction.type,
          interactionId: props.interaction.id,
          isAutomated: true,
          assignedById: authStore.identity.id,
          assignedToId: authStore.identity.id,
          isAdComment: props.interaction.isAdComment || false,
          messageBrandId: props.interaction.brandId,
          messageBrandName: getBrandHandleFromInteraction(props.interaction),
        });
      }
      inputTouched.value = true;
    }
  },
);

onMounted(() => {
  if (internalNotePage.value) {
    internalNoteMode.value = true;
  }
  if (interactionStore.shouldAutoFocusReply && replyComposer.value) {
    focusEnd();
    interactionStore.shouldAutoFocusReply = false;
  }
});

defineExpose({
  composerText,
  brandName,
});
</script>

<template>
  <div>
    <FormItem :disabled="disabled" :hint="maxLengthHint">
      <RichTextarea
        :key="internalNoteMode ? 'mentions-on' : 'mentions-off'"
        ref="replyComposer"
        v-model="composerText"
        :variant="variant"
        :placeholder="placeholder"
        :menu-items="menuItems"
        :readonly="translated && !internalNoteMode"
        :loading="translating"
        loading-text="Translating…"
        :auto-resize="false"
        :disabled="disabled || submitLoading"
        :max-length="maxLength"
        capture-enter
        plain-text
        v-bind="mentionProps"
        @enter="handleSubmit"
        @focus="onComposerFocus"
        @blur="isFocused = false"
      >
        <template #mentionOptionControl="{ option }">
          <Avatar v-bind="option.avatarProps" size="xs" />
        </template>
        <template v-if="showActions" #actions>
          <ToggleSwitch
            v-model="internalNoteMode"
            v-dash-tooltip="'Internal Notes are private messages only visible to your team'"
            label="Internal Note"
            :disabled="disabled"
          />
          <Button
            aria-label="Send"
            severity="primary"
            icon="send"
            size="xs"
            :disabled="sendDisabled"
            @click="handleSubmit"
          />
        </template>
      </RichTextarea>
    </FormItem>

    <EmojiPicker
      :key="internalNoteMode ? 'picker-mentions-on' : 'picker-mentions-off'"
      :dropdown="{
        targetNodes: '#reply-composer-emoji-picker-trigger',
        referenceNode: '#reply-composer-emoji-picker-trigger',
      }"
      @select="onEmojiPicked"
    />
    <SavedRepliesDropdown
      v-if="!internalNoteMode"
      v-model:shown="showReplies"
      :interaction="interaction"
      :dropdown="{
        targetNodes: '#saved-replies-trigger',
        referenceNode: '#saved-replies-trigger',
      }"
      @select="(reply) => insertSnippet(reply)"
    />
  </div>
</template>
